import React, { useState } from 'react';
import { Box, Stack, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import { Edit, Delete, Visibility, Download } from '@mui/icons-material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { suppliers, expenses } from '../data/resources';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const ResourcesPage = () => {
  const [filter, setFilter] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(filter.toLowerCase())
  );

  const filteredExpenses = expenses.filter(expense =>
    expense.supplier.toLowerCase().includes(filter.toLowerCase())
  );

  const handleSearchChange = (e) => {
    setFilter(e.target.value);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleDelete = (itemId) => {
    // Call delete API or update local state to remove item
    alert(`Item with ID ${itemId} deleted`);
  };

  const handleExtract = () => {
    // Handle extracting the data (e.g., to a CSV, PDF, etc.)
    alert("Data extracted!");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleView = (item) => {
    // Handle the view action (could open another dialog or navigate to a different page)
    alert(`Viewing details for ${item.name}`);
  };

  const handleExportPDF = () => {
    // Implement PDF export logic
    alert('Exporting as PDF');
  };

  const fontFamily = 'Cairo, Tajawal, sans-serif';

  const headCellSx = {
    fontWeight: 800,
    color: '#1E1B4B',
    fontFamily,
    fontSize: '0.875rem',
    bgcolor: '#F1F5F9',
    borderBottom: '1px solid #E5E7EB',
  };

  const bodyCellSx = {
    fontFamily,
    fontSize: '0.875rem',
    color: '#1E1B4B',
    borderBottom: '1px solid #E5E7EB',
  };

  const rowActionSx = (color, hoverBg) => ({
    color,
    borderRadius: 2,
    transition: 'all 0.2s ease',
    '&:hover': { bgcolor: hoverBg },
  });

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorefrontIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily }}
              >
                الحسابات والموردين
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily,
                }}
              >
                إدارة الموردين والمصروفات واستخراج البيانات
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExtract}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily,
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            استخراج البيانات
          </Button>
        </MotionPaper>

        {/* Search Bar */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <TextField
            label="بحث"
            variant="outlined"
            value={filter}
            onChange={handleSearchChange}
            fullWidth
            sx={{
              maxWidth: { xs: '100%', sm: 360 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                fontFamily,
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#4338CA' },
                '&.Mui-focused fieldset': { borderColor: '#4338CA' },
              },
              '& .MuiInputLabel-root': { fontFamily, color: '#6B7280' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
            }}
          />
        </MotionPaper>

        {/* Suppliers Table */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Stack useFlexGap
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #E5E7EB' }}
          >
            <StorefrontIcon sx={{ color: '#4338CA', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily }}>
              الموردين
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>اسم المورد</TableCell>
                  <TableCell sx={headCellSx}>الفئة</TableCell>
                  <TableCell sx={headCellSx}>الهاتف</TableCell>
                  <TableCell sx={headCellSx}>البريد الإلكتروني</TableCell>
                  <TableCell sx={headCellSx}>التحكم</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map((supplier) => (
                  <TableRow
                    key={supplier.id}
                    sx={{ transition: 'background 0.2s ease', '&:hover': { bgcolor: '#F8F9FF' } }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{supplier.name}</TableCell>
                    <TableCell sx={bodyCellSx}>{supplier.category}</TableCell>
                    <TableCell sx={bodyCellSx}>{supplier.phone}</TableCell>
                    <TableCell sx={bodyCellSx}>{supplier.email}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack useFlexGap direction="row" spacing={0.5}>
                        <Tooltip title="تعديل">
                          <IconButton size="small" onClick={() => handleEdit(supplier)} sx={rowActionSx('#4338CA', '#EEF2FF')}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton size="small" onClick={() => handleDelete(supplier.id)} sx={rowActionSx('#DC2626', '#FEE2E2')}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="عرض">
                          <IconButton size="small" onClick={() => handleView(supplier)} sx={rowActionSx('#0284C7', '#E0F2FE')}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MotionPaper>

        {/* Expenses Table */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Stack useFlexGap
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #E5E7EB' }}
          >
            <ReceiptLongIcon sx={{ color: '#4338CA', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily }}>
              المصروفات
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>نوع المصروف</TableCell>
                  <TableCell sx={headCellSx}>المبلغ</TableCell>
                  <TableCell sx={headCellSx}>التاريخ</TableCell>
                  <TableCell sx={headCellSx}>المورد</TableCell>
                  <TableCell sx={headCellSx}>التحكم</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow
                    key={expense.id}
                    sx={{ transition: 'background 0.2s ease', '&:hover': { bgcolor: '#F8F9FF' } }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{expense.type}</TableCell>
                    <TableCell sx={bodyCellSx}>{expense.amount}</TableCell>
                    <TableCell sx={bodyCellSx}>{expense.date}</TableCell>
                    <TableCell sx={bodyCellSx}>{expense.supplier}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack useFlexGap direction="row" spacing={0.5}>
                        <Tooltip title="تعديل">
                          <IconButton size="small" onClick={() => handleEdit(expense)} sx={rowActionSx('#4338CA', '#EEF2FF')}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton size="small" onClick={() => handleDelete(expense.id)} sx={rowActionSx('#DC2626', '#FEE2E2')}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="عرض">
                          <IconButton size="small" onClick={() => handleView(expense)} sx={rowActionSx('#0284C7', '#E0F2FE')}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MotionPaper>

        {/* Dialog for editing item */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          PaperProps={{
            sx: {
              borderRadius: 3,
              direction: 'rtl',
              fontFamily,
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              minWidth: { xs: '90%', sm: 420 },
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              color: '#1E1B4B',
              fontFamily,
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Edit sx={{ color: '#4338CA', fontSize: 22 }} />
            تعديل البيانات
          </DialogTitle>
          <DialogContent sx={{ pt: 3, fontFamily }}>
            {/* Form for editing */}
            {selectedItem && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontFamily, color: '#6B7280', fontSize: '0.875rem' }}>
                  {`Editing: ${selectedItem.name || selectedItem.type}`}
                </Typography>
                {/* Add form fields to edit the item */}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={() => alert('Item updated')}
              variant="contained"
              sx={{
                bgcolor: '#4338CA',
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                boxShadow: 'none',
                fontFamily,
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              حفظ التعديلات
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ResourcesPage;
